(() => {
  'use strict';
  if (window.SSPSizeSync) { window.SSPSizeSync.init(); return; }
  const norm = value => String(value ?? '').trim().toLowerCase();
  function targets(data, size, optionName) {
    const base = data.variant;
    if (!base) return { error: 'Product unavailable' };
    let linked = false;
    const values = data.options.map((option, index) => {
      const name = norm(option.name), key = norm(optionName);
      const sizeOption = name === key || name.endsWith(`(${key})`);
      const universal = option.values.length === 1 && ['one size', 'one size fits all', 'os', 'osfa'].includes(norm(option.values[0].name));
      if (sizeOption && !universal) {
        linked = true;
        return option.values.find(value => norm(value.name) === norm(size));
      }
      return option.values.find(value => norm(value.name) === norm(base.options[index]));
    });
    if (!linked) return { variant: base };
    if (!size) return { error: 'Select your size' };
    if (values.some(value => !value?.id)) return { error: `Unavailable in ${size}` };
    return { ids: values.map(value => value.id), names: values.map(value => value.name) };
  }
  const cache = new Map();
  const route = path => (window.Shopify?.routes?.root || '/').replace(/\/?$/, '/') + path;
  function productURL(handle, query) {
    const url = new URL(route(`products/${encodeURIComponent(handle)}`), location.origin);
    url.searchParams.set('view', 'ssp-size-variant');
    Object.entries(query).forEach(([key, value]) => url.searchParams.set(key, value));
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  }
  async function fetchVariant(data, plan, fresh = false) {
    const query = plan.ids ? { option_values: plan.ids.join(',') } : { variant: data.variant.id };
    const url = productURL(data.handle, query);
    const key = url.href;
    if (fresh) cache.delete(key);
    if (!cache.has(key)) {
      const request = fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(15000) })
        .then(async response => {
          if (!response.ok) throw new Error('Could not check availability. Please retry.');
          const result = JSON.parse((await response.text()).trim());
          return result.variant;
        }).catch(error => { cache.delete(key); throw error; });
      cache.set(key, request);
    }
    const variant = await cache.get(key);
    if (variant && plan.names && !plan.names.every((value, i) => norm(value) === norm(variant.options[i]))) {
      throw new Error('Size could not be verified. Please retry.');
    }
    if (variant && !plan.names && String(variant.id) !== String(data.variant.id)) {
      throw new Error('Variant could not be verified. Please retry.');
    }
    return variant;
  }
  class Widget {
    constructor(root) {
      this.root = root;
      this.scope = root.closest('.product-detail') || root.parentElement;
      this.q = selector => root.querySelector(selector);
      this.all = selector => [...root.querySelectorAll(selector)];
      this.optionName = root.dataset.sizeOption || 'Size';
      this.epoch = 0; this.loadingGrid = false; this.busy = false; this.cartTotal = 0;
      this.abort = new AbortController();
      this.mode = this.q('.quick-pick__card') ? 'quick' : 'mix';
      this.max = Number(this.q('.qty-tile.on')?.dataset.qty || 3);
      this.discount = Number(this.q('.qty-tile.on')?.dataset.discount || 0);
      this.button = this.q('.custom-atc-btn');
      this.buttonLabel = this.button?.textContent.trim() || 'ADD TO CART';
      this.cards = new WeakMap();
      this.collection = this.q('#mmGrid')?.dataset.defaultCollection;
      this.bind();
      this.prepareTiles();
      this.setMode(this.mode);
      this.filter();
      this.refreshCart().then(() => { if (this.root.isConnected && !this.busy) this.render(); });
      window.productUnit = { single: 'trunk', plural: 'trunks', itemLabel: 'Trunk' };
      this.sync();
    }
    message(text, retry = false) {
      const el = this.q('[data-ssp-size-message]');
      if (el) { el.textContent = text; el.hidden = !text; }
      const button = this.q('[data-ssp-size-retry]');
      if (button) button.hidden = !retry;
    }
    selectedSize() {
      const input = this.scope.querySelector('[data-ssp-underwear-size] input.js-option:checked');
      if (input) return input.value;
      const fields = [...this.scope.querySelectorAll('[data-single-option-selector]')];
      const field = fields.find(el => norm(el.querySelector('legend')?.textContent) === norm(this.optionName));
      return field?.querySelector('input:checked')?.value || '';
    }
    prepareTiles() {
      this.all('.quick-pick__card, .sock-tile').forEach(tile => {
        if (this.cards.has(tile)) return;
        let data;
        try { data = JSON.parse(tile.dataset.sspProduct); } catch (_) { data = null; }
        this.cards.set(tile, { data, pending: true, variant: null, error: '' });
        tile.classList.remove('is-disabled');
        tile.setAttribute('role', 'button'); tile.tabIndex = 0;
        const status = document.createElement('span');
        status.dataset.sspStatus = ''; tile.append(status);
      });
      this.q('.pack-selector')?.classList.remove('is-disabled');
      this.q('.mix-match')?.classList.remove('is-disabled');
    }
    async resolve(tile, size, fresh) {
      const state = this.cards.get(tile);
      if (!state?.data) return { variant: null, error: 'Product data missing', failed: true };
      const plan = targets(state.data, size, this.optionName);
      if (plan.error) return { variant: null, error: plan.error };
      try {
        const variant = plan.variant && !fresh ? plan.variant : await fetchVariant(state.data, plan, fresh);
        if (!variant?.available) return { variant: null, error: plan.ids ? `Unavailable in ${size}` : 'Sold out' };
        return { variant, error: '' };
      } catch (_) { return { variant: null, error: 'Could not check availability', failed: true }; }
    }
    async sync(fresh = false) {
      const epoch = ++this.epoch;
      const size = this.selectedSize();
      this.prepareTiles();
      const tiles = this.all('.quick-pick__card, .sock-tile');
      tiles.forEach(tile => { this.cards.get(tile).pending = true; tile.classList.add('ssp-size-pending'); });
      this.render();
      let index = 0;
      await Promise.all(Array.from({ length: Math.min(6, tiles.length) }, async () => {
        while (index < tiles.length && epoch === this.epoch && this.root.isConnected) {
          const tile = tiles[index++];
          const result = await this.resolve(tile, size, fresh);
          if (epoch !== this.epoch || !this.root.isConnected) return;
          Object.assign(this.cards.get(tile), result, { pending: false });
          this.paint(tile);
          this.render();
        }
      }));
    }
    paint(tile) {
      const state = this.cards.get(tile);
      tile.classList.toggle('ssp-size-pending', state.pending);
      tile.classList.toggle('ssp-size-unavailable', !state.pending && !state.variant);
      tile.setAttribute('aria-disabled', String(state.pending || !state.variant));
      tile.setAttribute('aria-pressed', String(tile.matches('.selected, .is-active')));
      tile.querySelector('[data-ssp-status]').textContent = state.pending ? 'Checking size…' : state.error;
      tile.querySelector('.oos-overlay')?.remove();
      if (state.variant && tile.matches('.quick-pick__card')) {
        tile.dataset.varId = state.variant.id;
        tile.dataset.price = state.variant.price;
        tile.dataset.compare = state.variant.compare_at_price || 0;
        tile.querySelector('.quick-pick__price').textContent = this.money(state.variant.price);
        const was = tile.querySelector('.quick-pick__compare-at-price');
        if (was) was.textContent = state.variant.compare_at_price > state.variant.price ? this.money(state.variant.compare_at_price) : '';
      }
    }
    money(cents) { return window.sspFormatMoney ? window.sspFormatMoney(cents) : new Intl.NumberFormat(undefined, { style:'currency', currency:window.Shopify?.currency?.active || 'AUD' }).format(cents / 100); }
    selection() { return this.mode === 'quick' ? this.all('.quick-pick__card.is-active') : this.all('.sock-tile.selected'); }
    render() {
      const selected = this.selection();
      const valid = selected.length > 0 && selected.every(tile => !this.cards.get(tile)?.pending && this.cards.get(tile)?.variant);
      const ready = valid && (this.mode === 'quick' || selected.length === this.max) && !this.loadingGrid && !this.collectionFailed;
      const qty = this.mode === 'quick' ? Number(selected[0]?.dataset.qty || 1) : selected.length;
      const subtotal = selected.reduce((sum, tile) => sum + (this.cards.get(tile)?.variant?.price || 0), 0);
      const compare = this.mode === 'quick' ? this.cards.get(selected[0])?.variant?.compare_at_price || 0 : subtotal;
      const price = this.mode === 'mix' ? Math.round(subtotal * (1 - this.discount / 100)) : subtotal;
      const write = (selector, text) => { const el = this.q(selector); if (el) el.textContent = text; };
      write('.summary-qty', `${qty} ${this.mode === 'quick' ? (qty === 1 ? 'trunk' : 'trunks') : (qty === 1 ? 'item' : 'items')}`);
      write('.summary-price', this.money(price));
      write('.summary-saving', compare > price ? `You save ${this.money(compare - price)}` : '');
      write('#mmCount', this.all('.sock-tile.selected').length);
      write('#mmMax', this.max);
      const remaining = this.max - this.all('.sock-tile.selected').length;
      write('#mmProgressText', remaining > 0 ? `Pick ${remaining} more${this.discount ? ` to unlock ${this.discount}% off` : ''}` : 'Selection complete');
      const progress = this.q('#mmProgress');
      if (progress) progress.style.width = `${Math.min(100, (this.max - remaining) / this.max * 100)}%`;
      if (this.button) {
        this.button.disabled = !ready || this.busy;
        this.button.style.opacity = this.button.disabled ? '.6' : '1';
        this.button.querySelector('.atc-text').textContent = this.busy ? 'Adding…' : this.buttonLabel;
      }
      const failures = selected.filter(tile => this.cards.get(tile)?.error);
      if (!this.busy) this.message(failures.length ? 'A selected product is unavailable. Change size or remove it from your selection.' : '', this.all('.quick-pick__card, .sock-tile').some(tile => this.cards.get(tile)?.failed));
      const active = selected[0];
      const detail = { variantId: ready && this.mode === 'quick' ? this.cards.get(active).variant.id : null, price, compare, qty, isComplete: ready, max: this.max, remaining, selectedItems: selected.map(tile => this.cards.get(tile)?.variant?.id).filter(Boolean), saving: active?.dataset.saving || '', duration:active?.dataset.duration || '', image:active?.dataset.image || '', widgetId:this.root.id };
      document.dispatchEvent(new CustomEvent(this.mode === 'quick' ? 'quick_pick_updated' : 'mix_match_updated', { detail }));
      this.shipping(price);
    }
    shipping(price) {
      const wrap = this.q('.ship-wrap'); if (!wrap) return;
      const threshold = Number(wrap.dataset.threshold); if (!(threshold > 0)) return;
      const total = this.cartTotal + price, remaining = Math.max(0, threshold - total);
      const fill = wrap.querySelector('#shipFill'), label = wrap.querySelector('#shipLbl'), sub = wrap.querySelector('#shipSub');
      if (fill) fill.style.width = `${Math.min(100, total / threshold * 100)}%`;
      if (label) label.textContent = remaining ? `${this.money(remaining)} away ${window.shipSettings?.initialSuffix || ''}` : window.shipSettings?.successText || 'Free shipping unlocked!';
      if (sub) sub.textContent = remaining ? window.shipSettings?.subText || 'Add more items to unlock free shipping' : window.shipSettings?.successSubText || 'Free standard shipping at checkout';
    }
    async refreshCart() {
      try { const response = await fetch(route('cart.js'), { cache:'no-store' }); if (response.ok) this.cartTotal = (await response.json()).total_price || 0; } catch (_) {}
    }
    setMode(mode) {
      this.mode = mode;
      this.all('.pack-tab').forEach(tab => {
        tab.type = 'button';
        const exists = tab.dataset.tab === 'quick' ? this.q('.quick-pick') : this.q('.mix-match');
        tab.hidden = !exists; tab.classList.toggle('is-active', tab.dataset.tab === mode);
      });
      this.all('.pack-pane').forEach(pane => pane.classList.toggle('is-active', pane.dataset.pane === mode));
      this.render();
    }
    filter() {
      const tag = norm(this.q('.fpill.on')?.dataset.tag || 'all'); let count = 0;
      this.all('.sock-tile').forEach(tile => {
        const show = tag === 'all' || (tile.dataset.tags || '').split(',').map(norm).includes(tag);
        tile.classList.toggle('hidden', !show); if (show) count++;
      });
      const empty = this.q('#mmEmpty'); if (empty) empty.style.display = count ? 'none' : 'block';
    }
    async loadCollection(handle) {
      this.collection = handle; this.loadingGrid = true; this.collectionFailed = false; ++this.epoch; this.render();
      const token = this.gridToken = Symbol();
      try {
        const url = new URL(route(`collections/${encodeURIComponent(handle)}`), location.origin);
        url.searchParams.set('view','ajax');
        const preview = new URL(location.href).searchParams.get('preview_theme_id'); if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { signal:AbortSignal.timeout(15000) });
        if (!response.ok) throw new Error();
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = doc.querySelector('#mmGrid'); if (!grid) throw new Error();
        if (this.gridToken !== token || !this.root.isConnected) return;
        this.q('#mmGrid').innerHTML = grid.innerHTML;
        this.prepareTiles(); this.filter(); this.loadingGrid = false; this.sync();
      } catch (_) {
        if (this.gridToken !== token) return;
        this.loadingGrid = false; this.collectionFailed = true; this.render();
        this.message('Could not load this collection. Please retry.', true);
      }
    }
    bind() {
      const opts = { signal:this.abort.signal };
      this.scope.addEventListener('change', event => {
        if (event.target.matches('.js-option') && event.target.closest('[data-single-option-selector]')) this.sync();
      }, opts);
      this.root.addEventListener('keydown', event => {
        const tile = event.target.closest('.quick-pick__card, .sock-tile');
        if (tile && ['Enter',' '].includes(event.key)) { event.preventDefault(); tile.click(); }
      }, opts);
      this.root.addEventListener('click', event => {
        const tab = event.target.closest('.pack-tab');
        if (tab) { event.preventDefault(); if (!this.busy) this.setMode(tab.dataset.tab); return; }
        const tile = event.target.closest('.quick-pick__card, .sock-tile');
        if (tile) {
          event.preventDefault();
          if (this.busy) return;
          const state = this.cards.get(tile);
          if (tile.matches('.sock-tile.selected')) tile.classList.remove('selected');
          else if (!state?.variant || state.pending) return;
          else if (tile.matches('.quick-pick__card')) {
            this.all('.quick-pick__card').forEach(card => card.classList.toggle('is-active', card === tile));
          } else if (this.all('.sock-tile.selected').length < this.max) tile.classList.add('selected');
          this.all('.quick-pick__card, .sock-tile').forEach(card => this.paint(card)); this.render(); return;
        }
        const qty = event.target.closest('.qty-tile');
        if (qty && !this.busy) {
          this.all('.qty-tile').forEach(item => item.classList.toggle('on', item === qty));
          this.max = Number(qty.dataset.qty); this.discount = Number(qty.dataset.discount) || 0;
          this.all('.sock-tile.selected').slice(this.max).forEach(item => item.classList.remove('selected'));
          this.render(); return;
        }
        const pill = event.target.closest('.fpill');
        if (pill) { event.preventDefault(); this.all('.fpill').forEach(item => item.classList.toggle('on', item === pill)); this.filter(); return; }
        if (event.target.closest('[data-ssp-size-retry]')) {
          if (this.collectionFailed) { this.collectionFailed = false; this.loadCollection(this.collection); }
          else this.sync(true);
        }
        if (event.target.closest('#browseAllBtn') && this.collection) location.href = route(`collections/${encodeURIComponent(this.collection)}`);
        if (event.target.closest('.custom-atc-btn')) { event.preventDefault(); this.add(); }
      }, opts);
      document.addEventListener('style_changed', event => {
        if (this.busy || !event.detail?.collection) return;
        const label = this.q('#mmStyleLabel'); if (label) label.textContent = event.detail.title || '';
        const empty = this.q('#mmEmptyStyle'); if (empty) empty.textContent = event.detail.title || '';
        this.loadCollection(event.detail.collection);
      }, opts);
    }
    async add() {
      if (this.busy || this.button?.disabled) return;
      this.busy = true;
      const selected = this.selection(), size = this.selectedSize(), mode = this.mode, epoch = this.epoch;
      this.render(); this.message('');
      let added = false;
      try {
        const results = await Promise.all(selected.map(tile => this.resolve(tile, size, true)));
        if (epoch !== this.epoch || size !== this.selectedSize()) throw new Error('Size changed. Please review your selection and try again.');
        results.forEach((result, index) => { Object.assign(this.cards.get(selected[index]), result); this.paint(selected[index]); });
        if (results.some(result => !result.variant)) throw new Error('A selected product is unavailable. Review your selection and try again.');
        const items = results.map((result, index) => ({ id:result.variant.id, quantity:1, ...(mode === 'mix' ? { properties:{ _Bundle:'Mix & Match', _Item:`Item ${index + 1}` } } : {}) }));
        const response = await fetch(route('cart/add.js'), { method:'POST', headers:{ 'Content-Type':'application/json' }, body:JSON.stringify({ items }) });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Could not add your selection. Please try again.');
        added = true;
        await this.refreshCart();
        if (typeof window.monster_refresh === 'function') window.monster_refresh(true);
        else if (typeof window.openCart === 'function') window.openCart();
        else if (window.MonsterCart?.open) window.MonsterCart.open();
        else {
          const link = document.querySelector('.header-cart a[href$="/cart"], a[href="/cart"]');
          if (link) link.click(); else location.href = route('cart');
        }
        document.dispatchEvent(new CustomEvent('ssp:size-cart-added', { detail:{ items:data.items || [], widgetId:this.root.id } }));
      } catch (error) {
        this.lastError = added ? 'Added to cart. Open your cart to review your items.' : error.message;
      } finally {
        this.busy = false; this.render();
        if (added) this.shipping(0);
        if (this.lastError) { this.message(this.lastError, !added); this.lastError = ''; }
      }
    }
    destroy() { ++this.epoch; this.abort.abort(); }
  }
  const instances = new Map();
  function init() {
    for (const [root, instance] of instances) if (!root.isConnected) { instance.destroy(); instances.delete(root); }
    document.querySelectorAll('[data-ssp-size-widget]').forEach(root => {
      if (!instances.has(root)) instances.set(root, new Widget(root));
    });
  }
  window.SSPSizeSync = { init, targets };
  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, instance] of instances) if (event.target.contains(root)) { instance.destroy(); instances.delete(root); }
  });
  if (document.readyState !== 'loading') init();
})();
